import capitalize from "lodash/capitalize";
import { useState } from "react";
import { useSelector } from "react-redux";
import { PEOPLE_FILTERS_TYPES } from "rivals/components/pages/TransferTracker/types";
import Button from "rivals/components/shared/Button";
import CollegeTypeahead from "rivals/components/shared/CollegeTypeahead";
import CustomButton from "rivals/components/shared/CustomButton";
import Drawer from "rivals/components/shared/Drawer";
import { selectPositionAbbreviations } from "rivals/redux/position/selectors";
import { PATHS } from "rivals/shared/constants";
import { useIsMobileWidth } from "rivals/shared/utils/mediaQuery";
import { setData } from "rivals/shared/utils/qa";
import { getAllAthleticYearsOptions } from "rivals/shared/utils/transfers";
import { genShallowPushToURL } from "rivals/shared/utils/urlUtils";
import ResponsiveFilter from "./ResponsiveFilter";
import styles from "./index.module.scss";
import strings from "./strings";
import { OwnProps } from "./types";
import { checkValue } from "./utils";
import { setDefaultFilters } from "../constants";

const {
  applyBtn,
  clearBtn,
  closeDrawerIconAlt,
  filterBy,
  placeholders,
  settingsIconAlt
} = strings;

const PeopleFilters = ({
  onChange,
  data = "",
  filters,
  applyFilters,
  year,
  sport
}: OwnProps): JSX.Element => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const positions = useSelector(selectPositionAbbreviations);
  const matchesMobile = useIsMobileWidth();
  const athleticYears = getAllAthleticYearsOptions();
  const appliedFiltersNumber = Object.values(filters).filter(
    item => !!item === true
  ).length;

  const onShowFiltersDrawer = (): void => {
    setDrawerOpen(true);
    applyFilters(false);
  };

  const onCloseFiltersDrawer = (): void => {
    setDrawerOpen(false);
  };

  const handleFilterChange = (
    value: string,
    name: PEOPLE_FILTERS_TYPES,
    apply: boolean
  ): void => {
    // Update filters when selecting
    const loweredCaseValue = value ? value.toLowerCase() : "";
    const newFilters = { ...filters, [name]: loweredCaseValue };
    onChange(newFilters);
    applyFilters(apply);
    apply &&
      genShallowPushToURL(
        PATHS.TRANSFER_TRACKER,
        { sport: sport, year: year.toString() },
        { ...newFilters }
      );
  };

  const handleClearFilters = (): void => {
    applyFilters(true);
    onChange(setDefaultFilters());
    genShallowPushToURL(
      PATHS.TRANSFER_TRACKER,
      { sport: sport, year: year.toString() },
      { ...setDefaultFilters() }
    );
    setDrawerOpen(false);
  };

  const handleApplyFilters = (): void => {
    // Apply filters - mobile only
    applyFilters(true);
    genShallowPushToURL(
      PATHS.TRANSFER_TRACKER,
      { sport: sport, year: year.toString() },
      { ...filters }
    );
    setDrawerOpen(false);
  };

  const positionsSelect = (
    <ResponsiveFilter
      className={styles.positionsSelect}
      data={`${data}-positions`}
      isMobile={matchesMobile}
      onChange={(value: string, apply = true): void =>
        handleFilterChange(value, PEOPLE_FILTERS_TYPES.TRANSFER_POSITION, apply)
      }
      options={positions}
      placeholder={placeholders.positions}
      value={checkValue(filters.transferPosition)}
    />
  );

  const originCollegesSelect = (
    <CollegeTypeahead
      allowClear
      defaultValue={capitalize(filters.originCollegeName)}
      dropdownClassName={styles.dropdownGray}
      filterStyles={true}
      onClear={(): void => {
        handleFilterChange(
          "",
          PEOPLE_FILTERS_TYPES.ORIGIN_COLLEGE,
          !matchesMobile
        );
      }}
      onSelected={(college): void => {
        handleFilterChange(
          college.commonName,
          PEOPLE_FILTERS_TYPES.ORIGIN_COLLEGE,
          !matchesMobile
        );
      }}
      placeholder={placeholders.originColleges}
      wrapperClassName={styles.collegesSelect}
      {...setData(data, "origin-colleges")}
    />
  );

  const exitCollegesSelect = (
    <CollegeTypeahead
      allowClear
      defaultValue={capitalize(filters.exitCollegeName)}
      dropdownClassName={styles.dropdownGray}
      filterStyles={true}
      onClear={(): void => {
        handleFilterChange(
          "",
          PEOPLE_FILTERS_TYPES.EXIT_COLLEGE,
          !matchesMobile
        );
      }}
      onSelected={(college): void => {
        handleFilterChange(
          college.commonName,
          PEOPLE_FILTERS_TYPES.EXIT_COLLEGE,
          !matchesMobile
        );
      }}
      placeholder={placeholders.exitColleges}
      wrapperClassName={styles.collegesSelect}
      {...setData(data, "exit-colleges")}
    />
  );

  const athleticYearsSelect = (
    <ResponsiveFilter
      className={styles.athleticYearsSelect}
      data={`${data}-athletic-years`}
      isMobile={matchesMobile}
      onChange={(value: string, apply = true): void =>
        handleFilterChange(value, PEOPLE_FILTERS_TYPES.ATHLETIC_YEAR, apply)
      }
      options={athleticYears}
      placeholder={placeholders.athleticYears}
      value={checkValue(filters.athleticYear)}
    />
  );

  const filtersDrawerTrigger = (
    <CustomButton
      buttonText={filterBy}
      className={styles.filtersDrawerTrigger}
      icon={
        <img alt={settingsIconAlt} src={"/static/icons/settings_icon.svg"} />
      }
      onClick={onShowFiltersDrawer}
      philter={`${data}-open-filters`}
    />
  );

  const drawerFooter = (
    <div className={styles.footerWrapper}>
      <Button
        buttonText={clearBtn}
        data={`${data}-clear-filters`}
        inverted
        onClick={handleClearFilters}
        size="small"
      />
      <Button
        buttonText={`${applyBtn}${
          appliedFiltersNumber ? ` (${appliedFiltersNumber})` : ""
        }`}
        data={`${data}-apply-filters`}
        onClick={handleApplyFilters}
        size="small"
      />
    </div>
  );

  const sideDrawer = (
    <Drawer
      className={styles.mobileDrawer}
      closeIcon={
        <img
          alt={closeDrawerIconAlt}
          src="/static/icons/close_modal_icon.svg"
          {...setData(data, "close-drawer-button")}
        />
      }
      footer={drawerFooter}
      headerStyle={{ borderBottom: "1px solid #d9d9d9", padding: "16px" }}
      onClose={onCloseFiltersDrawer}
      open={drawerOpen}
      title={filterBy}
      width={280}
    >
      <div className={styles.contentContainer}>
        <div className={styles.bodyWrapper}>
          {positionsSelect}
          {originCollegesSelect}
          {exitCollegesSelect}
          {athleticYearsSelect}
        </div>
      </div>
    </Drawer>
  );

  return (
    <div className={styles.peopleFiltersWrapper}>
      {!matchesMobile ? (
        <div>
          {positionsSelect}
          {originCollegesSelect}
          {exitCollegesSelect}
          {athleticYearsSelect}
        </div>
      ) : (
        <div>
          {filtersDrawerTrigger}
          {sideDrawer}
        </div>
      )}
    </div>
  );
};

export default PeopleFilters;
